/**
 * Shared toolbar for list pages (Product Library, Registrations, Claims, etc.).
 *
 * Two layout modes:
 * - "classic" (default): Two rows - Views row + Controls row. Used by Products Library, Partnerships.
 * - "compact": Single row - StatusPills + saved views (left) | Search + Columns + Export (right).
 *   Used by Registrations, Claims, and future entity pages following Clara Vale V2.
 */

import type { ReactNode } from 'react'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  ViewSwitcher,
  ColumnToggle,
  FilterBar,
  type DataTableColumn,
  type ActiveFilter,
} from '@/admin/components/data-table'
import type { TableView, TableViewConfig } from '@/admin/types/table-views'
import { StatusPills, type StatusPillItem } from '@/admin/components/shared/status-pills'
import {
  ProductFilterMenu,
  type ProductFilterFacet,
  type ProductFilterValue,
} from '@/shared/components/ui/product-filter-menu'
import { ExportMenu } from '@/admin/components/shared/export-menu'
import type { ExportScope } from '@/admin/lib/export-scope'

interface ListPageToolbarProps<T> {
  /** Unique table identifier (e.g. 'products', 'registrations') */
  tableId: string

  /** Layout mode - "classic" (default) or "compact" (Clara Vale V2) */
  layout?: 'classic' | 'compact'

  // Search
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string

  // Views
  views: TableView[]
  activeViewUuid: string | null
  onSelectView: (view: TableView | null) => void
  onSaveView: (name: string, config: TableViewConfig) => void
  onDeleteView: (uuid: string) => void
  onRenameView: (uuid: string, name: string) => void
  onSetOrgDefault?: (config: TableViewConfig) => void
  currentConfig: TableViewConfig
  /** The open view has changes not saved to it (see `useSavedListView`). */
  activeViewModified?: boolean
  /** The open view is seen by others and has changes waiting for an explicit save. */
  activeViewNeedsSave?: boolean
  onSaveActiveView?: () => void
  /** Whether the user may rename or delete a view (see `ViewSwitcher`). */
  canEditView?: (view: TableView) => boolean
  canManageDefaults?: boolean
  /**
   * Show the saved-views switcher (incl. the "save current view" input).
   * Defaults to true. Set false on surfaces that don't own per-table named
   * views - e.g. dashboard tiles, which get their views from the dashboard's
   * own role-level system and auto-persist column/filter prefs, so a per-tile
   * "save view" control would be redundant (and dead when unwired).
   */
  showViews?: boolean

  // Filters & columns
  columns: DataTableColumn<T>[]
  activeFilters: ActiveFilter[]
  onFiltersChange: (filters: ActiveFilter[]) => void
  /**
   * Show the generic column-filter (FILTER) button. Defaults to true. The
   * product library sets this false because its variant Filters fly-out is the
   * filtering surface there and two adjacent filter buttons read as redundant.
   */
  showColumnFilter?: boolean
  /**
   * Show the Columns toggle. Defaults to true. Set false on surfaces whose
   * column set is fixed (e.g. Inspections) so no Columns button renders.
   */
  showColumnToggle?: boolean
  columnVisibility: Record<string, boolean>
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void

  /**
   * Record-metadata filter fly-out (compact layout only). When facets are
   * provided, a Filters button renders immediately to the right of Columns.
   * Facets come from the list endpoint's ``filter_options`` block; the
   * selection is a map of facet key -> selected option values, which the page
   * merges into its query params. Empty/undefined facets render nothing.
   */
  filterFacets?: ProductFilterFacet[]
  filterValues?: ProductFilterValue
  onFilterValuesChange?: (values: ProductFilterValue) => void

  /** Column IDs that should be locked visible in the column picker */
  alwaysShownColumns?: string[]

  /** Column order - array of column ids for drag-and-drop reordering */
  columnOrder?: string[]
  onColumnOrderChange?: (order: string[]) => void

  /**
   * CSV export handler - when provided, shows a download button that opens a
   * scope picker (Current view / period presets / All time). The chosen
   * ``ExportScope`` is passed to the handler.
   */
  onExport?: (scope: ExportScope) => void
  isExporting?: boolean

  /** Extra elements rendered after Columns toggle (e.g. list/grid toggle) */
  trailingControls?: ReactNode

  // Compact layout only - status pills
  statusPills?: StatusPillItem[]
  activeStatusKey?: string | null
  onStatusSelect?: (key: string | null) => void
  statusTotalCount?: number
  /** Status-pill keys the user has dismissed - filtered out of the rendered pills. */
  dismissedStatusKeys?: string[]
  /** When provided, status pills show a dismiss × that calls this with the pill key. */
  onStatusDismiss?: (key: string) => void
}

export function ListPageToolbar<T>({
  layout = 'classic',
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  views,
  activeViewUuid,
  onSelectView,
  onSaveView,
  onDeleteView,
  activeViewModified,
  activeViewNeedsSave,
  onSaveActiveView,
  canEditView,
  onRenameView,
  onSetOrgDefault,
  currentConfig,
  canManageDefaults = false,
  showViews = true,
  columns,
  activeFilters,
  onFiltersChange,
  showColumnFilter = true,
  showColumnToggle = true,
  filterFacets,
  filterValues,
  onFilterValuesChange,
  columnVisibility,
  onColumnVisibilityChange,
  alwaysShownColumns,
  columnOrder,
  onColumnOrderChange,
  onExport,
  isExporting,
  trailingControls,
  statusPills,
  activeStatusKey,
  onStatusSelect,
  statusTotalCount,
  dismissedStatusKeys,
  onStatusDismiss,
}: ListPageToolbarProps<T>) {
  if (layout === 'compact') {
    const visibleStatusPills = dismissedStatusKeys?.length
      ? (statusPills ?? []).filter((pill) => !dismissedStatusKeys.includes(pill.key))
      : statusPills
    return (
      <div className="cv-data-table relative z-20 mb-5 grid grid-cols-1 items-start gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:gap-3">
        {/* Left: Status pills + saved views - wraps freely across rows */}
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {visibleStatusPills && onStatusSelect && (
            <StatusPills
              items={visibleStatusPills}
              activeKey={activeStatusKey ?? null}
              onSelect={onStatusSelect}
              totalCount={statusTotalCount ?? 0}
              onDismiss={onStatusDismiss}
            />
          )}
          {showViews && (
            <ViewSwitcher
              variant="compact"
              views={views}
              activeViewUuid={activeViewUuid}
              onSelectView={onSelectView}
              onSaveView={onSaveView}
              onDeleteView={onDeleteView}
              activeViewModified={activeViewModified}
              activeViewNeedsSave={activeViewNeedsSave}
              onSaveActiveView={onSaveActiveView}
              canEditView={canEditView}
              onRenameView={onRenameView}
              onSetOrgDefault={onSetOrgDefault}
              currentConfig={currentConfig}
              canManageDefaults={canManageDefaults}
            />
          )}
        </div>
        {/* Right: Columns + Search + Export - stays pinned to the top-right */}
        <div className="flex items-center gap-2 shrink-0 md:justify-self-end">
          {showColumnToggle && (
            <ColumnToggle
              columns={columns}
              visibility={columnVisibility}
              onVisibilityChange={onColumnVisibilityChange}
              alwaysShownColumns={alwaysShownColumns}
              columnOrder={columnOrder}
              onColumnOrderChange={onColumnOrderChange}
            />
          )}
          {filterFacets && filterValues && onFilterValuesChange && (
            <ProductFilterMenu
              facets={filterFacets}
              value={filterValues}
              onChange={onFilterValuesChange}
              triggerClassName="h-[28px] rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-2.5 text-[12px]"
            />
          )}
          <SearchInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={onSearchChange}
            iconSize={12}
            className="cv-data-table-body w-[120px] rounded-[2px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] py-[5px] pr-2.5 text-[var(--ink)] outline-none transition-[border-color] duration-150 focus:border-[var(--green)] md:w-[200px]"
          />
          {onExport && (
            <ExportMenu
              layout="compact"
              onExport={onExport}
              isExporting={isExporting}
              triggerClassName="h-[28px] w-[30px] rounded-[4px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-transparent p-0 text-[var(--ink-3)] transition-all duration-150 hover:border-[var(--rule-heavy)] hover:bg-transparent hover:text-[var(--ink-2)]"
            />
          )}
          {trailingControls}
        </div>
      </div>
    );
  }

  // Classic layout - unchanged
  return (
    <>
      {/* Views row */}
      {showViews && (
        <div className="cv-data-table mb-4 flex items-center gap-1 flex-wrap">
          <ViewSwitcher
            views={views}
            activeViewUuid={activeViewUuid}
            onSelectView={onSelectView}
            onSaveView={onSaveView}
            onDeleteView={onDeleteView}
            activeViewModified={activeViewModified}
            activeViewNeedsSave={activeViewNeedsSave}
            onSaveActiveView={onSaveActiveView}
            canEditView={canEditView}
            onRenameView={onRenameView}
            onSetOrgDefault={onSetOrgDefault}
            currentConfig={currentConfig}
            canManageDefaults={canManageDefaults}
          />
        </div>
      )}
      {/* Controls row - Search | spacer | Filter + Columns + extras */}
      <div className="cv-data-table mb-6 flex flex-wrap items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] pb-6">
        <div className="w-full md:max-w-xs">
          <SearchInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={onSearchChange}
            className="cv-data-table-body"
          />
        </div>

        <div className="flex-1" />

        {showColumnFilter && (
          <FilterBar
            columns={columns}
            activeFilters={activeFilters}
            onFiltersChange={onFiltersChange}
          />
        )}

        {showColumnToggle && (
          <ColumnToggle
            columns={columns}
            visibility={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
            alwaysShownColumns={alwaysShownColumns}
          />
        )}

        {onExport && (
          <ExportMenu
            layout="classic"
            onExport={onExport}
            isExporting={isExporting}
            triggerClassName="cv-data-table-compact rounded-[6px] border-[0.5px] border-solid border-[var(--rule-strong)] px-2.5 py-1.5 uppercase tracking-[0.1em] text-[var(--ink-2)] transition-colors duration-150 hover:border-[var(--ink-3)]"
          />
        )}

        {trailingControls}
      </div>
    </>
  );
}
